import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ApiClient } from '../api/client.js';
import type { CurrentUserResponse, LeagueRecord, PlayerRecord } from '../api/types.js';
import { recordBootstrapMetric, resetBootstrapPerformance } from './bootstrapPerformance.js';
import { mapError } from './errors.js';
import { extractClerkProfile, loadClerk, type ClerkLike } from './clerkRuntime.js';
import { loadRuntimeEnv } from './runtimeEnv.js';

const AUTH_BOOTSTRAP_TIMEOUT_MS = 10000;

export interface AppBannerState {
  type: 'success' | 'error' | 'warn' | 'info';
  message: string;
}

type BootstrapStatus = 'bootstrapping' | 'ready' | 'error';

interface AppStateContextValue {
  bootstrapStatus: BootstrapStatus;
  bootstrapError: string | null;
  clerk: ClerkLike | null;
  client: ApiClient | null;
  session: { userId: string; email?: string | null; name?: string | null } | null;
  player: PlayerRecord | null;
  leagues: LeagueRecord[];
  canCreateLeague: boolean;
  activeLeagueId: string | null;
  selectedSeasonIds: Map<string, string | null>;
  banner: AppBannerState | null;
  refreshIdentity: (force?: boolean) => Promise<void>;
  setBanner: (type: AppBannerState['type'], message: string) => void;
  clearBanner: () => void;
  setActiveLeague: (leagueId: string | null) => void;
  setSelectedSeasonId: (leagueId: string, seasonId: string | null) => void;
  resetSeasonSelection: (leagueId: string) => void;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AppStateContext = createContext<AppStateContextValue | null>(null);

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}

function createClient(clerk: ClerkLike): ApiClient {
  return new ApiClient({
    baseUrl: loadRuntimeEnv().VITE_API_BASE_URL,
    getToken: async () => {
      const token = await clerk.session?.getToken();
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      return token;
    },
  });
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [bootstrapStatus, setBootstrapStatus] = useState<BootstrapStatus>('bootstrapping');
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [clerk, setClerk] = useState<ClerkLike | null>(null);
  const [client, setClient] = useState<ApiClient | null>(null);
  const [session, setSession] = useState<{ userId: string; email?: string | null; name?: string | null } | null>(null);
  const [player, setPlayer] = useState<PlayerRecord | null>(null);
  const [leagues, setLeagues] = useState<LeagueRecord[]>([]);
  const [canCreateLeague, setCanCreateLeague] = useState(false);
  const [activeLeagueId, setActiveLeagueId] = useState<string | null>(localStorage.getItem('elm.activeLeagueId'));
  const [selectedSeasonIds, setSelectedSeasonIds] = useState<Map<string, string | null>>(new Map());
  const [banner, setBannerState] = useState<AppBannerState | null>(null);
  const identityPromiseRef = useRef<Promise<void> | null>(null);

  const setBanner = useCallback((type: AppBannerState['type'], message: string) => {
    setBannerState({ type, message });
  }, []);

  const clearBanner = useCallback(() => {
    setBannerState(null);
  }, []);

  const setActiveLeague = useCallback((leagueId: string | null) => {
    setActiveLeagueId(leagueId);
    if (leagueId) {
      localStorage.setItem('elm.activeLeagueId', leagueId);
    } else {
      localStorage.removeItem('elm.activeLeagueId');
    }
  }, []);

  const setSelectedSeasonId = useCallback((leagueId: string, seasonId: string | null) => {
    setSelectedSeasonIds((current) => {
      const next = new Map(current);
      next.set(leagueId, seasonId);
      return next;
    });
  }, []);

  const resetSeasonSelection = useCallback((leagueId: string) => {
    setSelectedSeasonIds((current) => {
      const next = new Map(current);
      next.delete(leagueId);
      return next;
    });
  }, []);

  const applyIdentity = useCallback(
    async (currentClient: ApiClient, currentClerk: ClerkLike) => {
      const meStartedAt = performance.now();
      const me = (await currentClient.getCurrentUser()) as CurrentUserResponse;
      recordBootstrapMetric('userMe', performance.now() - meStartedAt, {
        endpoint: '/api/v1/user/me',
        linkedPlayer: Boolean(me.player),
      });
      const clerkProfile = extractClerkProfile(currentClerk);

      setSession({
        userId: me.userId,
        email: me.email ?? clerkProfile.email ?? null,
        name: me.name ?? clerkProfile.name ?? null,
      });
      setPlayer(me.player);

      if (!me.player) {
        setLeagues([]);
        setCanCreateLeague(true);
        setActiveLeague(null);
        setSelectedSeasonIds(new Map());
        return;
      }

      const leaguesStartedAt = performance.now();
      const leaguesResult = await currentClient.getLeagues();
      recordBootstrapMetric('leagueLoad', performance.now() - leaguesStartedAt, {
        endpoint: '/api/v1/leagues',
        leagueCount: leaguesResult.leagues.length,
      });
      setLeagues(leaguesResult.leagues);
      setCanCreateLeague(leaguesResult.canCreateLeague);
      setActiveLeagueId((current) => {
        const stillValid = current ? leaguesResult.leagues.some((league) => league.id === current) : false;
        const next = stillValid ? current : (leaguesResult.leagues[0]?.id ?? null);
        if (next) {
          localStorage.setItem('elm.activeLeagueId', next);
        } else {
          localStorage.removeItem('elm.activeLeagueId');
        }
        return next;
      });
    },
    [setActiveLeague],
  );

  const refreshIdentity = useCallback(
    async (force: boolean = false) => {
      if (!clerk || !client) {
        return;
      }
      if (!clerk.session) {
        setSession(null);
        setPlayer(null);
        setLeagues([]);
        setCanCreateLeague(false);
        setActiveLeague(null);
        setSelectedSeasonIds(new Map());
        return;
      }
      if (!force && session) {
        return;
      }
      if (identityPromiseRef.current) {
        await identityPromiseRef.current;
        return;
      }

      const promise = (async () => {
        await applyIdentity(client, clerk);
      })();
      identityPromiseRef.current = promise;
      try {
        await promise;
      } finally {
        if (identityPromiseRef.current === promise) {
          identityPromiseRef.current = null;
        }
      }
    },
    [applyIdentity, clerk, client, session, setActiveLeague],
  );

  const signIn = useCallback(async () => {
    await clerk?.redirectToSignIn({ afterSignInUrl: window.location.href });
  }, [clerk]);

  const signOut = useCallback(async () => {
    if (!clerk) {
      return;
    }
    await clerk.signOut();
    setSession(null);
    setPlayer(null);
    setLeagues([]);
    setCanCreateLeague(false);
    setActiveLeague(null);
    setSelectedSeasonIds(new Map());
    clearBanner();
    window.history.replaceState({}, '', '/');
  }, [clearBanner, clerk, setActiveLeague]);

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        resetBootstrapPerformance(window.location.pathname);

        const clerkStartedAt = performance.now();
        const loadedClerk = await loadClerk();
        recordBootstrapMetric('clerkBootstrap', performance.now() - clerkStartedAt, {
          authenticated: Boolean(loadedClerk.session),
        });
        if (!active) {
          return;
        }
        const nextClient = createClient(loadedClerk);
        setClerk(loadedClerk);
        setClient(nextClient);
        if (loadedClerk.session) {
          const bootstrapStartedAt = performance.now();
          await withTimeout(
            applyIdentity(nextClient, loadedClerk),
            AUTH_BOOTSTRAP_TIMEOUT_MS,
            'Authentication bootstrap',
          );
          recordBootstrapMetric('authBootstrap', performance.now() - bootstrapStartedAt, {
            authenticated: true,
          });
        } else {
          setSession(null);
          setPlayer(null);
          setLeagues([]);
          setCanCreateLeague(false);
          setSelectedSeasonIds(new Map());
          recordBootstrapMetric('authBootstrap', 0, {
            authenticated: false,
          });
        }
        if (!active) {
          return;
        }
        setBootstrapStatus('ready');
      } catch (error) {
        if (!active) {
          return;
        }
        setBootstrapStatus('error');
        setBootstrapError(mapError(error));
      }
    })();

    return () => {
      active = false;
    };
  }, [applyIdentity]);

  const value = useMemo<AppStateContextValue>(
    () => ({
      bootstrapStatus,
      bootstrapError,
      clerk,
      client,
      session,
      player,
      leagues,
      canCreateLeague,
      activeLeagueId,
      selectedSeasonIds,
      banner,
      refreshIdentity,
      setBanner,
      clearBanner,
      setActiveLeague,
      setSelectedSeasonId,
      resetSeasonSelection,
      signIn,
      signOut,
    }),
    [
      activeLeagueId,
      banner,
      bootstrapError,
      bootstrapStatus,
      clerk,
      clearBanner,
      client,
      canCreateLeague,
      leagues,
      player,
      refreshIdentity,
      resetSeasonSelection,
      selectedSeasonIds,
      session,
      setActiveLeague,
      setBanner,
      setSelectedSeasonId,
      signIn,
      signOut,
    ],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): AppStateContextValue {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
}
